import {
	CancelOrderPayload,
	ConfirmOrderPayload,
	OrderStatus,
	PublicCvu,
} from "@crypto-payment-gateway/models"
import { tableOrderCurrencies } from "../columns/currencyImageCell"
import { tableOrderStatus } from "../columns/statusCell"
import { string } from "zod"

//MOVEMENTS ----------------------------------------------------------------------------------------------------------------------------
export type TableMovementType = "INBOUND" | "OUTBOUND"

export const tableMovementStatus = ["PROCESSED", "PENDING", "REJECTED"]

export type InboundArs = {
	id: string
	payment_id?: string
	type: Extract<TableMovementType, "INBOUND">
	status: (typeof tableMovementStatus)[number]
	currency: "ARS"
	time: Time
	amountReceived: string | undefined
	amountCredited: string | undefined
	amountDisplayInTable: string | undefined
	commission: MovementCommissionDetails
	orderId?: string
	store_name?: string
	recipient: FiatParty
	sender: FiatParty
	taxes: TransactionTax | undefined
	movement_type: "transfer"
	customer_id?: string
	tableView: "movements"
}

export type InboundCrypto = {
	id: string
	payment_id?: string
	type: Extract<TableMovementType, "INBOUND">
	status: (typeof tableMovementStatus)[number]
	currency: (typeof tableOrderCurrencies)[number]
	network: string
	time: Time
	amountReceived: string | undefined
	amountCredited: string | undefined
	amountCommission: string | undefined
	amountDisplayInTable: string | undefined
	commission: number //needs to be calculated manually as 1% of the amount
	orderId?: string
	store_name?: string
	recipient: CryptoParty
	sender: CryptoParty
	movement_type: "crypto"
	tableView: "movements"
}

export type OutboundArs = {
	id: string
	type: Extract<TableMovementType, "OUTBOUND">
	status: (typeof tableMovementStatus)[number]
	currency: (typeof tableOrderCurrencies)[number]
	time: Time
	amountSent: string
	amountDisplayInTable: string
	recipient: FiatParty
	sender: FiatParty
	movement_type: "transfer"
	tableView: "movements"
}

export type OutboundCrypto = {
	id: string
	type: Extract<TableMovementType, "OUTBOUND">
	status: (typeof tableMovementStatus)[number]
	currency: (typeof tableOrderCurrencies)[number]
	network: string
	time: Time
	amountSent: string | undefined
	amountDisplayInTable: string | undefined
	gas: string
	recipient: CryptoParty
	sender: CryptoParty
	movement_type: "crypto"
	tableView: "movements"
}

export type TableMovement =
	| InboundArs
	| InboundCrypto
	| OutboundArs
	| OutboundCrypto

//ORDERS ----------------------------------------------------------------------------------------------------------------------------
export type TableOrder = TableOrderArs | TableOrderCrypto

export type TableOrderArs = {
	id: string
	status: (typeof tableOrderStatus)[number]
	payment_options: PaymentOption[]
	time: Time
	creationDateTime: Time | undefined
	lastModifiedDateTime: Time | undefined
	expiration_timestamp?: Time | undefined
	display_id?: string | number
	network?: TableTransactionNetwork
	currency?: "ARS"
	currencyType: "fiat"
	expected?: Price
	received?: Price
	credited?: Price
	commission?: PaymentCommissionDetails
	amountDisplayInTable: string
	transactions: TableOrderSubTransactionArs[]
	recipient: FiatParty
	sender: FiatParty
	paymentUrl?: string
	store: StoreInfo | undefined
	store_name?: string
	store_id?: number
	taxes: TransactionTax | undefined
	tableView: "orders"
}

export type TableOrderCrypto = {
	id: string
	status: (typeof tableOrderStatus)[number]
	payment_options: PaymentOption[]
	time: Time
	creationDateTime: Time | undefined
	lastModifiedDateTime: Time | undefined
	expiration_timestamp?: Time | undefined
	display_id?: string | number
	network?: TableTransactionNetwork
	currency?: (typeof tableOrderCurrencies)[number]
	currencyType: "crypto"
	expected?: Price
	received?: Price
	receivedInRequestedCurrency?: Price
	credited?: Price
	creditedInRequestedCurrency?: Price
	commission?: PaymentCommissionDetails
	amountDisplayInTable: string
	transactions: TableOrderSubTransactionCrypto[]
	transactionHash?: string
	recipient: CryptoParty
	sender: CryptoParty
	paymentUrl?: string
	store: StoreInfo | undefined
	store_name?: string
	store_id?: number
	tableView: "orders"
}

export type TableOrderSubTransactionCrypto = {
	network: TableTransactionNetwork
	currency: TableTransactionCurrency
	amount: number | string
	credited: Price
	received: Price
	receivedInRequestedCurrency: Price
	creditedInRequestedCurrency: Price
	commission: PaymentCommissionDetails
	transactionHash: string
	transactionId: string
	sender: CryptoParty
	time: Time
}

export type TableOrderSubTransactionArs = {
	network: TableTransactionNetwork
	currency: TableTransactionCurrency
	amount: number | string
	credited: Price
	received: Price
	commission: PaymentCommissionDetails
	transactionId: string
	sender: FiatParty
	time: Time
	taxes?: TransactionTax
}

export type TableOrderSubTransaction =
	| TableOrderSubTransactionCrypto
	| TableOrderSubTransactionArs

//CUSTOMERS ----------------------------------------------------------------------------------------------------------------------------

export type TableCustomerType = "ACTIVE" | "INACTIVE"

export const tableCustomerStatus = ["ACTIVE", "INACTIVE"] as const

export interface TableCustomer {
	id: string
	name?: string
	customer_id: string
	alias: string
	bank_info: PublicCvu
	balance: number
	time: Time
	lastModifiedDateTime?: Time
	creationDateTime?: Time
	type: TableMovementType
	status: (typeof tableCustomerStatus)[number]
	contact_info: { email: string }
	amount: string
	currency: "ARS" // por ahora solo en ARS
	tax?: TransactionTax
	payment_id?: string
	recipient: FiatParty
	sender: FiatParty
	tableView: "customers"
}

//GENERIC ----------------------------------------------------------------------------------------------------------------------------

export type TableData = TableOrder | TableMovement | TableCustomer

export type TableView = "orders" | "movements" | "customers"

export type Time = {
	time: Date
	formatted: { date: string; time: string }
}

export type FiatParty = {
	cuit?: string
	titular?: string
	alias?: string
	cvu: string
	bank_entity?: {
		img: string | undefined
		name: string | undefined
		id: string | undefined
		entity_type: "psp" | "bank"
	}
	type: "fiat"
}

export type CryptoParty = {
	address: string | undefined
	type: "crypto"
}

export type TaxDescription = {
	tax_amount: number
	tax_type: TaxType
	tax_rate: number
}

export type MovementCommissionDetails = {
	commission: Price
	percent: number
	commissionTaxes?: TaxDescription[]
	commission_tax_total_amount?: string
}

export interface PaymentCommissionDetails {
	commission?: Price
	commissionInRequestedCurrency?: Price
	commissionTaxes?: TaxDescription[]
	commission_tax_total_amount?: string
	percent?: number
}

export enum TaxType {
	DEBIT = "DEBIT",
	CREDIT = "CREDIT",
	IIBB = "IIBB",
}

export const taxNames = {
	DEBIT: "Impuesto Débito",
	CREDIT: "Impuesto Crédito",
	IIBB: "Impuesto Ingresos Brutos",
}

export type TransactionTax = {
	taxes: TaxDescription[]
	total_tax_amount: string
}

export type PaymentHashTable = { [id: string]: string }

//ESTO TIENE QUE SER IMPORTADO MANUALMENTE PORQUE SINO SE ROMPE --> /models + enums = :(
export type PaymentType = "transfer" | "crypto" | "pix"

export type StoreType = "tiendanube" | "woocommerce" | "shopify"

export interface TiendanubeInfo {
	display_id: string | number | undefined
	store_id: number
	order_id: number
	order_number?: number
	client_email?: string
	order_status?: OrderStatus
	cancel_payload?: CancelOrderPayload
	confirm_payload?: ConfirmOrderPayload
	store_type: "tiendanube"
}

export interface ShopifyInfo {
	display_id: string | number | undefined
	store_id: string
	order_id: string
	app_id: string
	order_name?: string
	order_status?: OrderStatus
	cancel_payload?: CancelOrderPayload
	confirm_payload?: ConfirmOrderPayload
	store_type: "shopify"
}
export interface WoocommerceInfo {
	display_id: string | number | undefined
	store_id: number
	order_id: number
	client_email?: string
	store_type: "woocommerce"
}

export interface CustomStore {
	store_id: undefined
	display_id: string | number | undefined
	store_type: "custom"
}

export type StoreInfo =
	| TiendanubeInfo
	| ShopifyInfo
	| WoocommerceInfo
	| CustomStore

export type RecipientAccount = {
	account_type: string
	active_account: boolean
	address: string
	alias: string
	creation_timestamp: string
	cuit: string
	cvu_type: string
	entity_type: string
	last_modified_timestamp: string
	last_used_timestamp: string
	name: string
	psp_id: string
	bank_id: string
	user_id: string
}

export type Price = {
	amount: number | string
	currency: TableTransactionCurrency
}

export type TableTransactionStatus = (typeof tableOrderStatus)[number]

export type TableTransactionCurrency = (typeof tableOrderCurrencies)[number]

export type TableTransactionNetwork = "BSC" | "POLYGON" | "PIX" | "POLLUX"

export enum PaymentOption {
	TRANSFER = "transfer",
	CRYPTO = "crypto",
	PIX = "pix",
}

export interface ProcessingResult {
	order: TableOrder
	hashtableEntry: [string, string] // Tuple of [paymentId, orderId]
}

// export type TableOrder = {
// 	id: string
// 	orderId?: string | number
// 	currency?: (typeof tableOrderCurrencies)[number]
// 	status: (typeof tableOrderStatus)[number]
// 	lastModifiedDateTime: {
// 		date: string
// 		time: string
// 	}
// 	creationDateTime: {
// 		date: string
// 		time: string
// 	}
// 	receivedInRequestedCurrency?: Price
// 	sender?: string
// 	sender_cuit?: string
// 	sender_titular?: string
// 	buyer?: string
// 	expected?: Price
// 	received?: Price
// 	credited?: Price
// 	creditedInRequestedCurrency?: Price
// 	price?: Price
// 	commission?: Paymentcommission
// 	network?: TableTransactionNetwork
// 	address?: string
// 	alias?: string
// 	transactions?: TableSubOrder[]
// 	transactionHash?: string
// 	tiendanube?: TiendanubePaymentInfo
// 	woocommerce?: WoocommercePaymentInfo
// 	shopify?: ShopifyPaymentInfo
// 	paymentUrl?: string
// 	psp_id?: string
// 	bank_id?: string
// 	expiration_timestamp?: {
// 		date: string
// 		time: string
// 	}
// 	payment_options?: PaymentOption[]
// 	recipient_account?: RecipientAccount | undefined
// 	store_id?: number | string
// 	store_name?: string
// }
